/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
 */

/**
 * An area broker let's you access some predefined areas of a layout.
 * This areaBroker is compatible with @oat-sa/tao-core-sdk/core/areaBroker
 */

/**
 * Get area alias name :
 *  - from 'content' to 'getContentArea'
 *  - from 'header'  to 'getHeaderArea'
 * @param {string} areaKey - the area identifier
 * @param {string} [prefix='get'] - prefix
 * @returns {string} the alias name
 */
function createAlias(areaKey, prefix = 'get') {
    return `${prefix}${areaKey.charAt(0).toUpperCase() + areaKey.slice(1)}Area`;
}

/**
 * Register aliases to the broker for the given areas
 * @param {Object} broker - the areaBroker
 * @param {string[]} areaKeys - the areas identfiers
 */
function registerAliases(broker, areaKeys = []) {
    if (broker && typeof broker.getArea === 'function') {
        for (let areaKey of areaKeys) {
            broker[createAlias(areaKey, 'get')] = () => broker.getArea(areaKey);
            broker[createAlias(areaKey, 'clear')] = () => broker.clearAreaContent(areaKey);
        }
    }
}

/**
 * Un-register aliases to the broker for the given areas
 * @param {Object} broker - the areaBroker
 * @param {string[]} areaKeys - the areas identfiers
 */
function unregisterAliases(broker, areaKeys) {
    if (broker) {
        for (let areaKey of areaKeys) {
            delete broker[createAlias(areaKey, 'get')];
            delete broker[createAlias(areaKey, 'clear')];
        }
    }
}

/**
 * This factory creates an areaBroker that support setting the areas later.
 *
 * @param {HTMLElement} container - the main container
 * @returns {Object} the areaBroker
 * @throws {TypeError} if the container is not set
 */
export default function areaBrokerFactory(container) {
    let areas = {};

    return {
        /**
         * Access the main container
         * @returns {HTMLElement}
         */
        getContainer() {
            return container;
        },

        /**
         * Define the areas
         * @param {Object.<string, HTMLElement>} newAreas - the areas
         */
        setAreas(newAreas = {}) {
            unregisterAliases(this, Object.keys(areas));

            areas = newAreas;

            registerAliases(this, Object.keys(newAreas));
        },

        /**
         * Get the given area
         * @param {String} areaKey - the key of the area
         * @returns {HTMLElement?} the area element
         */
        getArea(areaKey) {
            if (areas && areas[areaKey] instanceof HTMLElement) {
                return areas[areaKey];
            }
            return null;
        },

        /**
         * Clears the content of the given area
         * @param {String} areaKey - the key of the area
         */
        clearAreaContent(areaKey) {
            if (areas && areas[areaKey] instanceof HTMLElement) {
                areas[areaKey].innerHTML = '';
            }
        },

        /**
         * Clears the content of a list of given areas
         * @param {String[]} areaKeys - the keys of the areas
         */
        clearAreasContent(areaKeys = []) {
            for (let areaKey of areaKeys) {
                this.clearAreaContent(areaKey);
            }
        }
    };
}
